<template>
	<div class="spe-monitor surface-0 flex align-items-center justify-content-center min-h-screen min-w-screen p-4">
		<div class="spe-monitor__card surface-card border-round-xl shadow-2">
			<div v-if="loading" class="text-center py-4">
				<ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
				<p class="mt-3 mb-0 text-600">{{ loadingLabel }}</p>
			</div>

			<div v-else-if="view === 'login'" class="spe-monitor__form">
				<div class="spe-monitor__header">
					<span class="spe-monitor__badge">DexanPassport</span>
					<h2 class="spe-monitor__title">Manager Login</h2>
					<p class="spe-monitor__context">
						Login with DexanPassport to review the dismiss request.
					</p>
				</div>

				<div class="spe-monitor__field">
					<label for="passportUsername" class="spe-monitor__label">
						Email / Username
						<span class="spe-monitor__required">*</span>
					</label>
					<InputText id="passportUsername" v-model="loginUsername" class="w-full" autocomplete="username"
						:disabled="submitting" />
				</div>

				<div class="spe-monitor__field">
					<label for="passportPassword" class="spe-monitor__label">
						Password
						<span class="spe-monitor__required">*</span>
					</label>
					<Password id="passportPassword" v-model="loginPassword" class="w-full" inputClass="w-full"
						:feedback="false" toggleMask autocomplete="current-password" :disabled="submitting" />
				</div>

				<small v-if="formError" class="p-error block mb-3">{{ formError }}</small>

				<Button label="Login with DexanPassport" icon="pi pi-sign-in" class="w-full spe-monitor__submit"
					:loading="submitting" @click="submitLogin" />
			</div>

			<div v-else-if="view === 'review'" class="spe-monitor__form">
				<div class="spe-monitor__header">
					<span class="spe-monitor__badge">Review</span>
					<h2 class="spe-monitor__title">Dismiss Request #{{ preview.scoringId }}</h2>
					<p class="spe-monitor__context">
						{{ preview.companyName }} · {{ preview.vendorName }} · {{ preview.period }}
					</p>
				</div>

				<div class="spe-monitor__readonly">
					<div class="spe-monitor__row">
						<span class="spe-monitor__row-label">Requester</span>
						<span>{{ preview.requester || '-' }}</span>
					</div>
					<div class="spe-monitor__row">
						<span class="spe-monitor__row-label">Submitted At</span>
						<span>{{ preview.requestedAt || '-' }}</span>
					</div>
					<div class="spe-monitor__row">
						<span class="spe-monitor__row-label">Status</span>
						<span>{{ statusLabel(preview.status) }}</span>
					</div>
					<div class="spe-monitor__row">
						<span class="spe-monitor__row-label">Score</span>
						<span>{{ preview.score || '-' }}</span>
					</div>
					<div class="spe-monitor__row spe-monitor__row--block">
						<span class="spe-monitor__row-label">Justification</span>
						<p class="mb-0 white-space-pre-wrap">{{ preview.justification || '-' }}</p>
					</div>
					<div class="spe-monitor__row spe-monitor__row--block">
						<span class="spe-monitor__row-label">SPE Report Document</span>
						<div class="flex align-items-center gap-2 flex-wrap">
							<span>SPE Report PDF</span>
							<Button label="View" icon="pi pi-eye" class="p-button-outlined p-button-sm"
								:loading="viewingReportPdf" @click="viewReportPdf" />
							<Button label="Download" icon="pi pi-download" class="p-button-text p-button-sm"
								:loading="downloadingReportPdf" @click="downloadReportPdf" />
						</div>
					</div>
					<div class="spe-monitor__row spe-monitor__row--block">
						<span class="spe-monitor__row-label">Supporting Document</span>
						<div v-if="preview.hasAttachment" class="flex align-items-center gap-2 flex-wrap">
							<span>{{ preview.attachmentName || 'attachment.pdf' }}</span>
							<Button label="View" icon="pi pi-eye" class="p-button-outlined p-button-sm"
								:loading="viewingAttachment" @click="viewAttachment" />
							<Button label="Download" icon="pi pi-download" class="p-button-text p-button-sm"
								:loading="downloading" @click="downloadAttachment" />
						</div>
						<span v-else>-</span>
					</div>
				</div>

				<!-- <div v-if="preview.history?.length" class="spe-monitor__history">
					<h3 class="spe-monitor__history-title">Approval History</h3>
					<ul class="spe-monitor__history-list">
						<li v-for="(item, idx) in preview.history" :key="idx">
							<strong>{{ item.type }}</strong>
							<span class="spe-monitor__history-meta">{{ item.created_date }}</span>
							<p class="mb-0 text-600">{{ item.description }}</p>
						</li>
					</ul>
				</div> -->

				<small v-if="formError" class="p-error block mb-2">{{ formError }}</small>

				<div v-if="preview.canDecide" class="spe-monitor__actions">
					<Button label="Reject" icon="pi pi-times" class="p-button-outlined p-button-danger"
						:disabled="submitting" @click="openReject" />
					<Button label="Approve" icon="pi pi-check" class="p-button-success"
						:loading="submitting && pendingAction === 'approve'" :disabled="submitting"
						@click="confirmApprove" />
				</div>
				<p v-else class="text-600 mb-0">This request is no longer waiting for a decision.</p>
			</div>

			<div v-else-if="view === 'reject-form'" class="spe-monitor__form">
				<div class="spe-monitor__header">
					<span class="spe-monitor__badge spe-monitor__badge--reject">Reject</span>
					<h2 class="spe-monitor__title">Reject Dismiss Request</h2>
				</div>

				<div class="spe-monitor__field">
					<label for="rejectReason" class="spe-monitor__label">
						Rejection Reason
						<span class="spe-monitor__required">*</span>
					</label>
					<Textarea id="rejectReason" v-model="rejectReason" rows="5" class="w-full" autoResize
						placeholder="Input rejection reason" :disabled="submitting" />
				</div>

				<small v-if="formError" class="p-error block mb-3">{{ formError }}</small>

				<div class="spe-monitor__actions">
					<Button label="Cancel" class="p-button-outlined" :disabled="submitting" @click="view = 'review'" />
					<Button label="Reject Request" icon="pi pi-times" class="p-button-danger" :loading="submitting"
						@click="submitReject" />
				</div>
			</div>

			<div v-else-if="view === 'success-approve'" class="text-center py-2">
				<div class="spe-monitor__status-icon spe-monitor__status-icon--success">
					<i class="pi pi-check" aria-hidden="true"></i>
				</div>
				<h2 class="mt-3 mb-2">Success Approve</h2>
				<p class="text-700 mb-0">{{ message }}</p>
			</div>

			<div v-else-if="view === 'success-reject'" class="text-center py-2">
				<div class="spe-monitor__status-icon spe-monitor__status-icon--reject">
					<i class="pi pi-times" aria-hidden="true"></i>
				</div>
				<h2 class="mt-3 mb-2">Dismiss Rejected</h2>
				<p class="text-700 mb-0">{{ message }}</p>
			</div>

			<div v-else class="text-center py-2">
				<div class="spe-monitor__status-icon spe-monitor__status-icon--error">
					<i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
				</div>
				<h2 class="mt-3 mb-2">Unable to process</h2>
				<p class="text-red-700 mb-0">{{ message }}</p>
			</div>
		</div>

		<Dialog v-model:visible="showAttachmentPreview" :header="attachmentPreviewName || 'Document Preview'"
			:modal="true" appendTo="body" :style="{ width: '85vw' }" :maximizable="true"
			class="spe-monitor-pdf-dialog" @hide="clearAttachmentPreview">
			<div v-if="viewingAttachment || viewingReportPdf" class="flex justify-content-center p-6">
				<ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
			</div>
			<iframe v-else-if="attachmentPreviewUrl" :src="attachmentPreviewUrl" class="spe-monitor-pdf-preview"
				title="PDF Preview" />
			<div v-else class="p-4 text-center text-color-secondary">
				Failed to load PDF preview.
			</div>
			<template #footer>
				<Button label="Download PDF" icon="pi pi-download" class="p-button-success mr-2"
					:disabled="!attachmentPreviewBlob || viewingAttachment || viewingReportPdf"
					@click="downloadAttachmentFromPreview" />
				<Button label="Close" icon="pi pi-times" class="p-button-outlined" @click="showAttachmentPreview = false" />
			</template>
		</Dialog>
	</div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
	authenticateSpeV2DismissApprover,
	clearDismissApproverSession,
	downloadSpeV2DismissAttachment,
	downloadSpeV2DismissReportPdf,
	getDismissApproverSession,
	previewSpeV2DismissDecision,
	setDismissApproverSession,
	submitSpeV2DismissDecision,
} from '../../service/SpeV2PublicDismissApi';

const route = useRoute();
const router = useRouter();
const loading = ref(true);
const loadingLabel = ref('Loading approval...');
const submitting = ref(false);
const downloading = ref(false);
const downloadingReportPdf = ref(false);
const viewingAttachment = ref(false);
const viewingReportPdf = ref(false);
const pendingAction = ref('');
const view = ref('error');
const message = ref('');
const formError = ref('');
const rejectReason = ref('');
const loginUsername = ref('');
const loginPassword = ref('');
const preview = ref({});
const sessionToken = ref('');
const showAttachmentPreview = ref(false);
const attachmentPreviewUrl = ref('');
const attachmentPreviewBlob = ref(null);
const attachmentPreviewName = ref('');
const reviewToken = ref(String(route.query.token || '').trim());

const token = () => String(reviewToken.value || route.query.token || '').trim();

const applyRefreshedReviewToken = (nextToken) => {
	const refreshed = String(nextToken || '').trim();
	if (!refreshed || refreshed === token()) return;
	reviewToken.value = refreshed;
	router.replace({
		query: {
			...route.query,
			token: refreshed,
		},
	});
};

const statusLabel = (status) => {
	const map = { O: 'Outstanding', PD: 'Waiting Approval', D: 'Approved / Dismissed', R: 'Rejected' };
	return map[status] || status || '-';
};

const applyDecisionResult = (action, payload) => {
	const ok = payload?.status === 'success';
	message.value = payload?.message || (ok ? 'Decision saved.' : 'Failed to process decision.');
	if (!ok) {
		view.value = 'error';
		return;
	}
	view.value = action === 'approve' ? 'success-approve' : 'success-reject';
};

const loadPreview = async (activeSessionToken) => {
	const previewRes = await previewSpeV2DismissDecision(token(), activeSessionToken);
	if (previewRes.data?.status !== 'success') {
		message.value = previewRes.data?.message || 'Failed to load dismiss approval.';
		view.value = 'error';
		return false;
	}

	preview.value = previewRes.data.data || {};
	applyRefreshedReviewToken(preview.value.reviewToken);
	if (preview.value.status === 'D') {
		message.value = 'SPE report is already dismissed.';
		view.value = 'success-approve';
		return true;
	}
	if (preview.value.status === 'O' && preview.value.decidedBy) {
		message.value = 'Dismiss request is already rejected.';
		view.value = 'success-reject';
		return true;
	}

	view.value = 'review';
	return true;
};

const submitLogin = async () => {
	const username = String(loginUsername.value || '').trim();
	const password = String(loginPassword.value || '');
	if (!username || !password) {
		formError.value = 'Email/username and password are required.';
		return;
	}

	formError.value = '';
	submitting.value = true;
	try {
		const res = await authenticateSpeV2DismissApprover(token(), { username, password });
		if (res.data?.status !== 'success') {
			formError.value = res.data?.message || 'Login failed.';
			submitting.value = false;
			return;
		}

		const data = res.data.data || {};
		sessionToken.value = data.sessionToken;
		applyRefreshedReviewToken(data.reviewToken);
		setDismissApproverSession({
			sessionToken: data.sessionToken,
			email: data.email,
			displayName: data.displayName,
			scoringId: data.scoringId,
		});
		loginPassword.value = '';
		await loadPreview(data.sessionToken);
	} catch (err) {
		formError.value = err?.response?.data?.message || 'DexanPassport login failed.';
	}
	submitting.value = false;
};

const confirmApprove = async () => {
	if (!window.confirm('Are you sure you want to approve this Dismiss Request?')) {
		return;
	}

	formError.value = '';
	submitting.value = true;
	pendingAction.value = 'approve';
	try {
		const res = await submitSpeV2DismissDecision(token(), sessionToken.value, { action: 'approve' });
		applyDecisionResult('approve', res.data);
	} catch (err) {
		message.value = err?.response?.data?.message || 'Failed to approve dismiss request.';
		view.value = 'error';
	}
	submitting.value = false;
	pendingAction.value = '';
};

const openReject = () => {
	formError.value = '';
	rejectReason.value = '';
	view.value = 'reject-form';
};

const submitReject = async () => {
	const reason = String(rejectReason.value || '').trim();
	if (!reason) {
		formError.value = 'Rejection reason is required.';
		return;
	}

	formError.value = '';
	submitting.value = true;
	pendingAction.value = 'reject';
	try {
		const res = await submitSpeV2DismissDecision(token(), sessionToken.value, {
			action: 'reject',
			rejectReason: reason,
		});
		applyDecisionResult('reject', res.data);
	} catch (err) {
		message.value = err?.response?.data?.message || 'Failed to reject dismiss request.';
		view.value = 'error';
	}
	submitting.value = false;
	pendingAction.value = '';
};

const base64ToBlob = (base64, mimeType) => {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i += 1) {
		bytes[i] = binary.charCodeAt(i);
	}
	return new Blob([bytes], { type: mimeType || 'application/pdf' });
};

const fetchAttachmentBlob = async () => {
	const res = await downloadSpeV2DismissAttachment(token(), sessionToken.value);
	if (res.data?.status !== 'success' || !res.data?.data?.file) {
		throw new Error(res.data?.message || 'Failed to load attachment.');
	}
	const { file, filename, mimeType } = res.data.data;
	return {
		blob: base64ToBlob(file, mimeType),
		filename: filename || preview.value.attachmentName || 'dismiss-supporting.pdf',
	};
};

const fetchReportPdfBlob = async () => {
	const res = await downloadSpeV2DismissReportPdf(token(), sessionToken.value);
	if (res.data?.status !== 'success' || !res.data?.data) {
		throw new Error(res.data?.message || 'Failed to generate SPE Report PDF.');
	}
	const file = typeof res.data.data === 'string' ? res.data.data : res.data.data?.file;
	if (!file) {
		throw new Error(res.data?.message || 'Failed to generate SPE Report PDF.');
	}
	const filename =
		res.data.filename ||
		(typeof res.data.data === 'object' ? res.data.data?.filename : null) ||
		`SPE_Report_${preview.value.scoringId || 'dismiss'}.pdf`;
	return {
		blob: base64ToBlob(file, 'application/pdf'),
		filename,
	};
};

const clearAttachmentPreview = () => {
	if (attachmentPreviewUrl.value) {
		URL.revokeObjectURL(attachmentPreviewUrl.value);
	}
	attachmentPreviewUrl.value = '';
	attachmentPreviewBlob.value = null;
	attachmentPreviewName.value = '';
};

const openPdfPreview = async ({ fetchBlob, loadingRef, defaultName }) => {
	formError.value = '';
	clearAttachmentPreview();
	loadingRef.value = true;
	showAttachmentPreview.value = true;
	try {
		const { blob, filename } = await fetchBlob();
		attachmentPreviewBlob.value = blob;
		attachmentPreviewName.value = filename || defaultName;
		attachmentPreviewUrl.value = URL.createObjectURL(blob);
	} catch (err) {
		formError.value = err?.response?.data?.message || err?.message || 'Failed to open PDF.';
		showAttachmentPreview.value = false;
	}
	loadingRef.value = false;
};

const viewAttachment = async () => {
	await openPdfPreview({
		fetchBlob: fetchAttachmentBlob,
		loadingRef: viewingAttachment,
		defaultName: 'dismiss-supporting.pdf',
	});
};

const viewReportPdf = async () => {
	await openPdfPreview({
		fetchBlob: fetchReportPdfBlob,
		loadingRef: viewingReportPdf,
		defaultName: 'SPE_Report.pdf',
	});
};

const triggerBlobDownload = (blob, filename) => {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename || 'document.pdf';
	anchor.click();
	setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const downloadAttachment = async () => {
	formError.value = '';
	downloading.value = true;
	try {
		const { blob, filename } = await fetchAttachmentBlob();
		triggerBlobDownload(blob, filename);
	} catch (err) {
		formError.value = err?.response?.data?.message || err?.message || 'Failed to download attachment.';
	}
	downloading.value = false;
};

const downloadReportPdf = async () => {
	formError.value = '';
	downloadingReportPdf.value = true;
	try {
		const { blob, filename } = await fetchReportPdfBlob();
		triggerBlobDownload(blob, filename);
	} catch (err) {
		formError.value = err?.response?.data?.message || err?.message || 'Failed to download SPE Report PDF.';
	}
	downloadingReportPdf.value = false;
};

const downloadAttachmentFromPreview = () => {
	if (!attachmentPreviewBlob.value) return;
	triggerBlobDownload(
		attachmentPreviewBlob.value,
		attachmentPreviewName.value || 'document.pdf'
	);
};

onMounted(async () => {
	if (!token()) {
		loading.value = false;
		message.value = 'Approval link is missing a token.';
		view.value = 'error';
		return;
	}

	const existing = getDismissApproverSession();
	if (existing?.sessionToken) {
		sessionToken.value = existing.sessionToken;
		try {
			const ok = await loadPreview(existing.sessionToken);
			if (!ok) {
				clearDismissApproverSession();
				sessionToken.value = '';
				view.value = 'login';
			}
		} catch (err) {
			clearDismissApproverSession();
			sessionToken.value = '';
			const status = err?.response?.status;
			if (status === 401 || status === 403) {
				view.value = 'login';
			} else {
				message.value = err?.response?.data?.message || 'Failed to process dismiss approval.';
				view.value = 'error';
			}
		}
		loading.value = false;
		return;
	}

	view.value = 'login';
	loading.value = false;
});

onBeforeUnmount(() => {
	clearAttachmentPreview();
});
</script>

<style scoped>
.spe-monitor__card {
	max-width: 40rem;
	width: 100%;
	padding: 1.75rem 1.5rem;
}

.spe-monitor__form {
	display: flex;
	flex-direction: column;
	gap: 1.15rem;
}

.spe-monitor__header {
	margin-bottom: 0.15rem;
}

.spe-monitor__badge {
	display: inline-flex;
	align-items: center;
	padding: 0.2rem 0.65rem;
	border-radius: 999px;
	background: #ccfbf1;
	color: #115e59;
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.03em;
	text-transform: uppercase;
	margin-bottom: 0.55rem;
}

.spe-monitor__badge--reject {
	background: #ffedd5;
	color: #9a3412;
}

.spe-monitor__title {
	margin: 0 0 0.55rem;
	font-size: 1.35rem;
	color: #111827;
	line-height: 1.3;
}

.spe-monitor__context {
	margin: 0;
	padding: 0.65rem 0.8rem;
	border-radius: 10px;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	color: #4b5563;
	font-size: 0.82rem;
	line-height: 1.45;
	word-break: break-word;
}

.spe-monitor__field {
	display: flex;
	flex-direction: column;
	gap: 0.45rem;
}

.spe-monitor__label {
	font-weight: 600;
	color: #374151;
	font-size: 0.95rem;
}

.spe-monitor__required {
	color: #dc2626;
	margin-left: 0.1rem;
}

.spe-monitor__readonly {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	padding: 0.85rem 0.9rem;
	border: 1px solid #e5e7eb;
	border-radius: 10px;
	background: #fff;
}

.spe-monitor__row {
	display: grid;
	grid-template-columns: 9rem 1fr;
	gap: 0.5rem;
	align-items: start;
	font-size: 0.9rem;
}

.spe-monitor__row--block {
	grid-template-columns: 1fr;
}

.spe-monitor__row-label {
	font-weight: 600;
	color: #6b7280;
}

.spe-monitor__history-title {
	margin: 0 0 0.5rem;
	font-size: 1rem;
}

.spe-monitor__history-list {
	margin: 0;
	padding-left: 1.1rem;
	display: flex;
	flex-direction: column;
	gap: 0.65rem;
}

.spe-monitor__history-meta {
	margin-left: 0.4rem;
	color: #9ca3af;
	font-size: 0.8rem;
}

.spe-monitor__actions {
	display: flex;
	justify-content: space-between;
	gap: 0.75rem;
	flex-wrap: wrap;
}

.spe-monitor__submit {
	margin-top: 0.25rem;
}

.spe-monitor__status-icon {
	width: 3.5rem;
	height: 3.5rem;
	border-radius: 999px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto;
}

.spe-monitor__status-icon .pi {
	font-size: 1.5rem;
	color: #ffffff;
}

.spe-monitor__status-icon--success {
	background: #22c55e;
}

.spe-monitor__status-icon--reject {
	background: #f97316;
}

.spe-monitor__status-icon--error {
	background: #ef4444;
}
</style>

<style>
.spe-monitor-pdf-dialog .spe-monitor-pdf-preview {
	width: 100%;
	min-height: 70vh;
	border: 0;
	border-radius: 8px;
	background: #f3f4f6;
}
</style>
